import { useState, useEffect, useCallback } from 'react';
import { ActiveCampaign, CampaignSettings, CampaignReport } from '@/components/admin/campaigns/types';
import { generateCampaignId } from '@/utils/campaignUtils';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/SupabaseAuthContext';
import { logger } from '@/utils/logger';

type Row = any;

const rowToCampaign = (r: Row): ActiveCampaign => {
  const settings = (r.settings || {}) as Record<string, unknown>;
  return {
    id: r.id,
    campaignId: r.id,
    globalCampaignId: r.global_campaign_id,
    subType: r.sub_type,
    title: r.title,
    subtitle: r.subtitle ?? '',
    referrerReward: Number(r.referrer_reward ?? 0),
    refereeReward: Number(r.referee_reward ?? 0),
    currency: r.currency ?? 'USD',
    shareOptions: Array.isArray(r.share_options) ? r.share_options : [],
    showAffiliatePortal: !!r.show_affiliate_portal,
    customMessages: (r.custom_messages ?? {}) as Record<string, string>,
    collectPhoneNumber: !!r.collect_phone_number,
    limitReferrals: !!r.limit_referrals,
    showTerms: !!r.show_terms,
    termsContent: r.terms_content ?? '',
    programDescription: r.program_description ?? '',
    rewardInfo: r.reward_info ?? '',
    campaignType: r.campaign_type ?? undefined,
    status: r.status,
    isActive: !!r.is_active,
    startDate: new Date(r.start_date),
    referralCount: r.referral_count ?? 0,
    conversionRate: Number(r.conversion_rate ?? 0),
    totalRewards: Number(r.total_rewards ?? 0),
    createdAt: new Date(r.created_at),
    updatedAt: new Date(r.updated_at),
    ...(settings as Partial<CampaignSettings>),
  };
};

const campaignToRow = (
  s: Partial<CampaignSettings>,
  adminId: string,
  globalCampaignId: string,
) => {
  const {
    affiliateSettings, milestoneRewards, newsletterSettings,
    leaderboardSettings, ecommerceSettings,
  } = s;
  return {
    admin_id: adminId,
    global_campaign_id: globalCampaignId,
    sub_type: s.subType ?? 'single',
    title: s.title ?? 'Untitled Campaign',
    subtitle: s.subtitle,
    referrer_reward: s.referrerReward ?? 0,
    referee_reward: s.refereeReward ?? 0,
    currency: s.currency ?? 'USD',
    share_options: s.shareOptions ?? [],
    show_affiliate_portal: !!s.showAffiliatePortal,
    custom_messages: s.customMessages ?? {},
    collect_phone_number: !!s.collectPhoneNumber,
    limit_referrals: !!s.limitReferrals,
    show_terms: !!s.showTerms,
    terms_content: s.termsContent,
    program_description: s.programDescription,
    reward_info: s.rewardInfo,
    campaign_type: s.campaignType,
    settings: {
      affiliateSettings, milestoneRewards, newsletterSettings,
      leaderboardSettings, ecommerceSettings,
    },
    is_active: s.isActive ?? true,
  };
};

const rowToReport = (r: Row): CampaignReport => ({
  id: `report-${r.id}`,
  globalCampaignId: r.global_campaign_id,
  adminId: r.admin_id,
  superAdminId: r.super_admin_id ?? '',
  campaignName: r.title,
  startDate: new Date(r.start_date),
  endDate: r.end_date ? new Date(r.end_date) : undefined,
  totalReferrals: r.referral_count ?? 0,
  successfulReferrals: Math.floor((r.referral_count ?? 0) * Number(r.conversion_rate ?? 0)),
  conversionRate: Number(r.conversion_rate ?? 0),
  totalRewards: Number(r.total_rewards ?? 0),
  status: r.status,
  createdAt: new Date(r.created_at),
  updatedAt: new Date(r.updated_at),
});

export const useCampaigns = () => {
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState<ActiveCampaign[]>([]);
  const [campaignReports, setCampaignReports] = useState<CampaignReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await (supabase as any)
      .from('campaigns')
      .select('*')
      .order('created_at', { ascending: false });
    if (err) {
      logger.error('Failed to load campaigns', err);
      setError(err.message);
      setCampaigns([]);
      setCampaignReports([]);
    } else {
      const rows = (data ?? []) as Row[];
      setCampaigns(rows.map(rowToCampaign));
      setCampaignReports(rows.map(rowToReport));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const createCampaign = async (settings: CampaignSettings): Promise<ActiveCampaign> => {
    if (!user?.id) throw new Error('Not authenticated');
    const globalCampaignId = settings.globalCampaignId || generateCampaignId();
    const payload = campaignToRow(settings, user.id, globalCampaignId);
    const { data, error: err } = await (supabase as any)
      .from('campaigns')
      .insert(payload)
      .select()
      .single();
    if (err) throw err;
    const created = rowToCampaign(data);
    setCampaigns(prev => [created, ...prev]);
    setCampaignReports(prev => [rowToReport(data), ...prev]);
    return created;
  };

  const updateCampaign = async (id: string, updates: Partial<ActiveCampaign>): Promise<ActiveCampaign> => {
    const patch: Record<string, unknown> = {};
    if (updates.title !== undefined) patch.title = updates.title;
    if (updates.subtitle !== undefined) patch.subtitle = updates.subtitle;
    if (updates.status !== undefined) patch.status = updates.status;
    if (updates.isActive !== undefined) patch.is_active = updates.isActive;
    if (updates.referrerReward !== undefined) patch.referrer_reward = updates.referrerReward;
    if (updates.refereeReward !== undefined) patch.referee_reward = updates.refereeReward;
    if (updates.currency !== undefined) patch.currency = updates.currency;
    if (updates.shareOptions !== undefined) patch.share_options = updates.shareOptions;
    if (updates.showAffiliatePortal !== undefined) patch.show_affiliate_portal = updates.showAffiliatePortal;
    if (updates.customMessages !== undefined) patch.custom_messages = updates.customMessages;
    if (updates.collectPhoneNumber !== undefined) patch.collect_phone_number = updates.collectPhoneNumber;
    if (updates.limitReferrals !== undefined) patch.limit_referrals = updates.limitReferrals;
    if (updates.showTerms !== undefined) patch.show_terms = updates.showTerms;
    if (updates.termsContent !== undefined) patch.terms_content = updates.termsContent;
    if (updates.programDescription !== undefined) patch.program_description = updates.programDescription;
    if (updates.rewardInfo !== undefined) patch.reward_info = updates.rewardInfo;
    if (updates.subType !== undefined) patch.sub_type = updates.subType;
    if (updates.campaignType !== undefined) patch.campaign_type = updates.campaignType;
    if (updates.referralCount !== undefined) patch.referral_count = updates.referralCount;
    if (updates.conversionRate !== undefined) patch.conversion_rate = updates.conversionRate;
    if (updates.totalRewards !== undefined) patch.total_rewards = updates.totalRewards;
    if (updates.status === 'completed') patch.end_date = new Date().toISOString();

    const { data, error: err } = await (supabase as any)
      .from('campaigns')
      .update(patch)
      .eq('id', id)
      .select()
      .single();
    if (err) throw err;
    const updated = rowToCampaign(data);
    setCampaigns(prev => prev.map(c => (c.id === id ? updated : c)));
    setCampaignReports(prev => prev.map(r => (r.id === `report-${id}` ? rowToReport(data) : r)));
    return updated;
  };

  const toggleCampaignStatus = async (id: string): Promise<ActiveCampaign> => {
    const campaign = campaigns.find(c => c.id === id);
    if (!campaign) throw new Error('Campaign not found');
    const newStatus = campaign.status === 'active' ? 'paused' : 'active';
    return updateCampaign(id, { status: newStatus, isActive: newStatus === 'active' });
  };

  const getCampaignReportsByAdmin = (adminId: string) =>
    campaignReports.filter(r => r.adminId === adminId);
  const getCampaignReportsBySuperAdmin = (superAdminId: string) =>
    campaignReports.filter(r => r.superAdminId === superAdminId);
  const getCampaignReportByGlobalId = (globalId: string) =>
    campaignReports.find(r => r.globalCampaignId === globalId);

  return {
    campaigns,
    campaignReports,
    loading,
    error,
    reload: load,
    createCampaign,
    updateCampaign,
    toggleCampaignStatus,
    getCampaignReportsByAdmin,
    getCampaignReportsBySuperAdmin,
    getCampaignReportByGlobalId,
  };
};

export default useCampaigns;
